import type { DailyTacticalReadiness } from '../../services/operationsApi';
import type { DtsWeeklyAudit, DtsAuditSection } from '../../services/fableLabApi';
import { cohortLabel } from './fableLabels';

/** 주간 감사 3종의 구현 여부와 실제 통과 여부를 보여주는 패널. */
export default function FableAudits({ readiness, audit, auditError }: Props) {
  const rows = buildRows(readiness, audit);
  return (
    <section className="fable-panel">
      <header className="fable-panel-head">
        <h3>기록을 믿어도 되나 (주간 감사)</h3>
        <p>
          {audit
            ? `대상 기록 묶음: ${cohortLabel(audit.cohort_id)} (${audit.cohort_id}) · 표본 ${audit.sample_run_count}건`
            : auditError
              ? `API 오류: ${auditError}`
              : '데이터 없음'}
        </p>
      </header>
      <ul className="fable-audit-list">
        {rows.map((row) => (
          <li key={row.internal} className={`fable-audit tone-${row.tone}`}>
            <div>
              <strong>{row.label}</strong>
              <code>{row.internal}</code>
            </div>
            <span>{row.state}</span>
            <p>{row.note}</p>
          </li>
        ))}
      </ul>
    </section>
  );
}

function auditState(section: DtsAuditSection | undefined, implemented: boolean) {
  if (!implemented) return { state: '미구현', tone: 'danger' };
  if (!section) return { state: '구현됨 · 결과 데이터 없음', tone: 'warn' };
  if (section.rows.length === 0 && !section.passed) {
    return { state: '구현됨 · 아직 점검 표본 없음', tone: 'info' };
  }
  return section.passed
    ? { state: '통과', tone: 'safe' }
    : { state: '통과 못 함 — 확인 필요', tone: 'warn' };
}

function buildRows(readiness: DailyTacticalReadiness, audit: DtsWeeklyAudit | null) {
  const ready = readiness.audit_readiness;
  const defs = [
    {
      label: '저장 무결성 점검',
      internal: 'storage integrity audit',
      implemented: Boolean(ready.weekly_storage_integrity_audit_implemented),
      section: audit?.storage_integrity_audit,
      note: '저장된 근거를 다시 계산해도 같은 값이 나오는지(위·변조 없는지) 확인합니다.',
    },
    {
      label: '모델 재현 점검',
      internal: 'model rerun audit',
      implemented: Boolean(ready.weekly_model_rerun_audit_implemented),
      section: audit?.model_rerun_audit,
      note: '같은 질문으로 모델을 다시 호출해 판단 구조가 흔들리는지 확인합니다. 재호출은 별도 승인 후 수동으로만 실행합니다.',
    },
    {
      label: '근거 사실 점검',
      internal: 'fact-check audit',
      implemented: Boolean(ready.weekly_fact_check_audit_implemented),
      section: audit?.fact_check_audit,
      note: '뉴스·시세·개장 전 시세 근거가 실제로 저장되어 검증 가능한지 확인합니다.',
    },
  ];
  return defs.map((def) => {
    const { state, tone } = auditState(def.section, def.implemented);
    return { ...def, state, tone };
  });
}

type Props = {
  readiness: DailyTacticalReadiness;
  audit: DtsWeeklyAudit | null;
  auditError: string | null;
};
